import { AbstractControl } from '@angular/forms';

import { AlphaNumericWithSeparators } from '../validations';
import {
  DIGITS_ONLY_PATTERN,
  SSN_FORMAT_PATTERN,
  TRACK1_FORMAT_PATTERN,
  TRACK2_FORMAT_PATTERN,
  ZIP_FORMAT_PATTERN,
} from '../validations/regex.validations';
import { entitlementRequirementsErrorMessage } from './entitlements.interface';

export interface ErrorMessageObject {
  fieldKey: string;
  errorName: string;
  message: string | ErrorMessageFn;
}

type ErrorMessageFn = (error: FieldValidationErrorObject) => string;

export interface FieldValidationErrorObject {
  control_name: string | null;
  error_name: string;
  error_value: any;
  default_message?: string;
  message?: string;
  parent_control?: string | null;
  isNested?: boolean;
  control_index?: number;
}

export interface FormGroupControls {
  [key: string]: AbstractControl;
}

type ErrorMessages = Record<string, string>;

const formatUTCDate = (date: Date): string => {
  if (isNaN(date?.getTime())) return '';
  const mm = String(date.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(date.getUTCDate()).padStart(2, '0');
  const yyyy = date.getUTCFullYear();
  return `${mm}-${dd}-${yyyy}`;
};

const PATTERN_MESSAGES: Record<string, string> = {
  [AlphaNumericWithSeparators.toString()]:
    'Only letters, numbers, spaces, underscores (_) and dashes (-) are allowed',
  [DIGITS_ONLY_PATTERN.toString()]: 'Only numeric digits are allowed',
  [SSN_FORMAT_PATTERN.toString()]: 'Please enter a valid SSN',
  [ZIP_FORMAT_PATTERN.toString()]: 'Please enter a valid ZIP code',
  [TRACK1_FORMAT_PATTERN.toString()]: 'Please enter a valid Track 1 format',
  [TRACK2_FORMAT_PATTERN.toString()]: 'Please enter a valid Track 2 format',
};

export const getControlErrorMessage = (
  control: AbstractControl | null
): string => {
  if (!control?.errors) {
    return '';
  }

  const firstErrorKey = Object.keys(control.errors)[0];

  const allMessages = getControlErrors(control);

  return allMessages[firstErrorKey] || 'Invalid field';
};

export const getControlErrors = (
  control?: AbstractControl | null
): ErrorMessages => {
  // If control is null or has no errors, return empty object (or default safe values)
  const errors = control?.errors || {};

  let patternMessage = 'The format is invalid'; // Default fallback message
  if (errors['pattern']) {
    const requiredPattern = errors['pattern'].requiredPattern;

    // Look up the message in our map. If not found, it keeps the default.
    if (PATTERN_MESSAGES[requiredPattern]) {
      patternMessage = PATTERN_MESSAGES[requiredPattern];
    }
  }

  return {
    required: 'This field is required',

    // Dynamic: Safely access .requiredLength using optional chaining (?.)
    minlength: `Minimum length is ${errors['minlength']?.requiredLength} characters`,
    maxlength: `Maximum length is ${errors['maxlength']?.requiredLength} characters`,

    pattern: patternMessage,
    email: 'Please enter a valid email address',
    invalidSlackAddress:
      'The value must be either a channel starting with (#) or a valid email address',
    alreadyExistsChip: 'This value already exists in the list',
    invalidIpRange: 'Please enter only valid IP Ranges',
    invalidIpRangePrivate: 'Private IP address are not permitted.',
    invalidIpRangeClass: 'Class D or E IP address are not permitted.',
    invalidDomain: 'Please enter only valid domain names',
    invalidEmail: 'Please enter only valid email formats',
    invalidEmailPublicDomain: 'Please enter only emails with corporate domains',
    invalidBin: 'Please enter only valid BIN numbers',
    invalidPhone: 'Please enter only valid phone numbers',
    invalidNoQuotes: 'Quotes are not allowed',
    invalidNumericOnly: 'Only numbers allowed',
    invalidFractionalNumber:
      'Please enter only valid Fractional Numbers with the following pattern ##-####/####',
    invalidEmailDomain:
      'Can not add domain, since it is a known email provider',
    invalidRepeated: 'This element is repeated inside the list',
    valueTooLarge: 'This value exceeds the allowed limit',
    valueTooSmall: 'This value is below the allowed limit',
    min: `Value must be at least ${errors['min']?.min}`,
    max: `Value cannot exceed ${errors['max']?.max}`,
    atLeastOneInputHasData: `At least one of these fields must have data: ${errors['atLeastOneInputHasData']?.fields?.join(', ') ?? 'N/A'}`,

    // Card field validation errors
    invalidExpiration: 'Please enter a valid expiration date (MM/YY)',
    invalidDob: 'Please enter a valid DOB (YYYY-MM-DD)',
    invalidDobFuture: 'Please enter a valid DOB',
    invalidIpList: 'Please enter valid IPv4 addresses',
    invalidPrice: 'Please enter a valid non-negative price',
    notInteger: 'Please enter a whole number',
    notFinite: 'Please enter a number this field can hold',
    duplicate: 'Option already selected',

    invalidEntitlementRequirement: entitlementRequirementsErrorMessage(
      errors['invalidEntitlementRequirement']?.dependents ?? []
    ),
    invalidCustomFeedback:
      'Each deliverable needs at least one status and one action while custom feedback is on',
    incompleteCustomFeedback:
      'Custom feedback has an item missing a label or an option',

    emptyWallet: 'Pick a currency or enter a wallet id',
    victimRequired: 'Pick a client or enter a victim name',
    invalidWalletAddress: `Not a valid ${errors['invalidWalletAddress']?.currency ?? 'wallet'} address`,
    shopNameQuotes:
      'Source name should not contain quotes for a source of type Shop',
    subSourceNameRequired: 'Subsource name is required',
    invalidSourceUrl: 'Url must be valid',

    invalidDate: 'Please enter a valid date',
    invalidYear: `Year must be ${errors['invalidYear']?.minYear ?? 2015} or later`,
    invalidMaxYear: `Year must be ${errors['invalidMaxYear']?.maxYear ?? new Date().getFullYear()} or earlier`,
    minDate: `Date must be on or after ${formatUTCDate(errors['minDate']?.min ? new Date(errors['minDate'].min) : new Date())}`,
    maxDate: `Date must be on or before ${formatUTCDate(errors['maxDate']?.max ? new Date(errors['maxDate'].max) : new Date())}`,
    matDatepickerMin: `Date must be on or after ${formatUTCDate(errors['matDatepickerMin']?.min ? new Date(errors['matDatepickerMin'].min) : new Date())}`,
    matDatepickerMax: `Date must be on or before ${formatUTCDate(errors['matDatepickerMax']?.max ? new Date(errors['matDatepickerMax'].max) : new Date())}`,
    matDatepickerParse: 'Invalid date format',
    invalidUrl: 'Please enter a valid URL',
    invalidCelExpression:
      errors['invalidCelExpression']?.message ??
      'Please enter a valid CEL expression',
    disallowedCharacters: `Do not include the following characters: ${errors['disallowedCharacters']?.join(' ') ?? ''}`,
  };
};
